import { User } from '@/models/User';
import { STORAGE_KEY } from './storageKeys';
import { decryptAES, encryptAES } from './crypto/AES';
import { encryptMD5 } from './crypto/MD5';
import { COOKIE_KEY } from './Constants';
import { Basket, BasketCampaignData } from '@/models/Basket';

const baseOptsForCookies = {
  maxAge: 25 * 365 * 24 * 60 * 60,
  path: '/',
  sameSite: 'lax' as const,
  secure: true,
};

export const getCookieForApp = (name: string): string | undefined => {
  try {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        const value = cookie.substring(nameEQ.length);
        return decodeURIComponent(value);
      }
    }

    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const setCookieForApp = (name: string, value: string): void => {
  try {
    const { maxAge, path = '/', sameSite = 'Lax' } = baseOptsForCookies;

    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secure = isHttps;

    const encodedValue = encodeURIComponent(value);
    let cookieString = `${name}=${encodedValue}`;

    if (maxAge !== undefined) cookieString += `; Max-Age=${maxAge}`;
    if (path) cookieString += `; Path=${path}`;
    if (sameSite) cookieString += `; SameSite=${sameSite}`;
    if (secure) cookieString += '; Secure';

    document.cookie = cookieString;
  } catch (error) {}
};

export const removeCookieForApp = (name: string, path: string = '/'): void => {
  try {
    const { secure, sameSite } = baseOptsForCookies;

    // expires + Max-Age=0 birlikte daha sağlam
    let cookieString = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;

    if (sameSite) cookieString += `; SameSite=${sameSite}`;
    if (secure) cookieString += `; Secure`;

    document.cookie = cookieString;
  } catch (error) {}
};

export const getTokenForApp = (): string | undefined => {
  const token = getCookieForApp(COOKIE_KEY.TOKEN);
  return token ? decryptAES(token) : undefined;
};

export const setTokenForApp = (token: string, maxAge?: number): void => {
  setCookieForApp(COOKIE_KEY.TOKEN, encryptAES(token));
};

export const removeTokenForApp = (): void => {
  removeCookieForApp(COOKIE_KEY.TOKEN);
};

export const getLocalUserForApp = (): User | undefined => {
  const userKey = encryptMD5(STORAGE_KEY.LOCAL_USER);
  const userValue = getCookieForApp(userKey);
  return userValue ? JSON.parse(decryptAES(userValue)) : undefined;
};

export const setLocalUserForApp = (user: User | undefined) => {
  const userKey = encryptMD5(STORAGE_KEY.LOCAL_USER);
  if (!user) {
    removeCookieForApp(userKey);
    return;
  }
  setCookieForApp(userKey, encryptAES(JSON.stringify(user)));
};

export const getOsType = (): string | undefined => {
  return getCookieForApp('hzc_os_type');
};

export const getAppVersionCode = (): string | undefined => {
  return getCookieForApp('hzc_app_version_code');
};

export const setBasketForApp = (basketKey: string, encryptedBasket?: string) => {
  if (!encryptedBasket) {
    removeCookieForApp(basketKey);
    return;
  }

  setCookieForApp(basketKey, encryptedBasket);
};

export const getBasketForApp = (basketKey?: string): Basket | undefined => {
  const keyForBasket = basketKey ?? encryptMD5(STORAGE_KEY.BASKET);
  const basketValue = getCookieForApp(keyForBasket);
  return basketValue ? JSON.parse(decryptAES(basketValue)) : undefined;
};

export const getCampaignDataForApp = (campaignDataKey?: string): BasketCampaignData | undefined => {
  const keyForCampaignData = campaignDataKey ?? encryptMD5(STORAGE_KEY.CAMPAIGN_DATA);
  const campaignDataValue = getCookieForApp(keyForCampaignData);
  return campaignDataValue
    ? (JSON.parse(decryptAES(campaignDataValue)) as BasketCampaignData)
    : undefined;
};

export const setCampaignDataForApp = (
  campaignDataKey: string,
  encryptedCampaignData?: string,
) => {
  if (!encryptedCampaignData) {
    removeCookieForApp(campaignDataKey);
    return;
  }

  setCookieForApp(campaignDataKey, encryptedCampaignData);
};
